import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../AppIcon';
import Image from '../AppImage';
import { getTierInfo as getTierInfoFromConfig } from '../../utils/tierConfig';


const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userProfile, signOut, refreshProfile } = useAuth();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  // Derive data from userProfile (fetched via AuthContext from Supabase)
  const avatarUrl = userProfile?.avatar_url || null;
  const tierData = getTierInfoFromConfig(userProfile?.subscription_tier);
  const remainingCredits = userProfile?.remaining_credits ?? 0;
  const monthlyCredits = userProfile?.monthly_credits ?? tierData.monthlyCredits;
  const subscriptionTier = userProfile?.subscription_tier || 'free';
  const userRole = userProfile?.role || 'user';
  const firstName = userProfile?.first_name || '';
  const lastName = userProfile?.last_name || '';
  const email = userProfile?.subscription_tier === 'team_member' ? '' : (userProfile?.email || '');

  // Credit percentage for the progress bar
  const creditPercent = monthlyCredits > 0 ? Math.round((remainingCredits / monthlyCredits) * 100) : 0;

  // User initials fallback for avatar
  const getUserInitials = () => {
    if (firstName && lastName) return `${firstName[0]}${lastName[0]}`.toUpperCase();
    if (firstName) return firstName[0].toUpperCase();
    if (email) return email[0].toUpperCase();
    return 'U';
  };

  const getTierInfo = () => getTierInfoFromConfig(subscriptionTier);

  const baseNavigationItems = [
    { label: 'Dashboard', path: '/main-dashboard', icon: 'LayoutDashboard' },
    { label: 'Projects', path: '/project-history', icon: 'FolderOpen' },
  ];

  const navigationItems = userRole === 'admin'
    ? [...baseNavigationItems, { label: 'Admin Panel', path: '/admin-dashboard', icon: 'Shield' }]
    : baseNavigationItems;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef?.current && !userMenuRef?.current?.contains(event?.target)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const handleToggleMobileMenu = () => setIsMobileMenuOpen((prev) => !prev);
    window.addEventListener('toggle-mobile-menu', handleToggleMobileMenu);
    return () => window.removeEventListener('toggle-mobile-menu', handleToggleMobileMenu);
  }, []);

  const handleLogout = async () => {
    await signOut();
    navigate('/login-registration');
    setIsUserMenuOpen(false);
  };

  const isActive = (path) => location?.pathname === path;

  return (
    <>
      <div
        className={`mobile-menu-overlay ${isMobileMenuOpen ? 'open' : ''}`}
        onClick={() => setIsMobileMenuOpen(false)} />

      <div className={`mobile-menu-panel ${isMobileMenuOpen ? 'open' : ''}`}>
        <div className="p-5 pt-6">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-8 px-1">
            <div className="w-10 h-10 rounded-[12px] overflow-hidden shadow-lg flex-shrink-0">
              <img
                src="/assets/images/tg_image_1314028779-1770779087120.png"
                alt="Roomwork.ai Logo"
                className="w-full h-full object-cover" />
            </div>
            <span className="font-bold text-[rgba(255,252,239,1)] text-base">Roomwork.ai</span>
          </div>

          {/* Navigation - Account Settings Style */}
          <nav className="glassmorphic-card rounded-2xl overflow-hidden">
            {[...navigationItems, { label: 'Settings', path: '/user-profile-settings', icon: 'Settings' }].map((item) => {
              const active = isActive(item?.path);
              return (
                <Link
                  key={item?.path}
                  to={item?.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`relative w-full flex items-center gap-3 px-4 py-3.5 text-left transition-all duration-300 ${
                    active ? 'text-orange-400' : 'text-white/70 hover:text-white'
                  }`}
                  style={active ? { background: 'rgba(254, 131, 56, 0.08)' } : {}}
                >
                  {active && (
                    <div
                      className="absolute left-0 top-0 bottom-0 w-[3px]"
                      style={{
                        background: 'linear-gradient(to bottom, transparent, rgba(254, 131, 56, 0.8) 30%, rgba(254, 131, 56, 0.8) 70%, transparent)',
                        boxShadow: '0 0 8px rgba(254, 131, 56, 0.4), 0 0 20px rgba(254, 131, 56, 0.15)',
                      }}
                    />
                  )}
                  <Icon name={item?.icon} size={20} />
                  <span className="font-medium text-sm">{item?.label}</span>
                </Link>
              );
            })}

            <button
              onClick={handleLogout}
              className="relative w-full flex items-center gap-3 px-4 py-3.5 text-left transition-all duration-300 text-white/70 hover:text-white"
            >
              <Icon name="LogOut" size={20} />
              <span className="font-medium text-sm">Logout</span>
            </button>
          </nav>
        </div>
      </div>
      <header className="fixed top-0 left-0 right-0 z-1000 hidden lg:block h-16 px-4 pt-4">
        <div className="container mx-auto px-4 h-full">
          <div className="flex items-center justify-between h-full bg-white/10 backdrop-blur-sm rounded-3xl px-6">
            {/* Left Section: Logo */}
            <div className="flex items-center">
              <Link
                to="/main-dashboard"
                className="flex items-center gap-3 transition-all duration-300">

                <div className="w-9 h-9 rounded-[14px] overflow-hidden shadow-lg">
                  <img
                    src="/assets/images/tg_image_1314028779-1770779087120.png"
                    alt="Roomwork.ai Logo"
                    className="w-full h-full object-cover" />

                </div>
                <span className="font-bold bg-gradient-to-r from-orange-500 via-orange-400 to-orange-500 bg-clip-text text-[rgba(255,252,239,1)] text-base">Roomwork.ai</span>
              </Link>
            </div>

            {/* Center Section: Dashboard & Projects - Plain Text Centered with Bold Animation */}
            <nav className="absolute left-1/2 transform -translate-x-1/2 flex items-center gap-6">
              {navigationItems?.map((item) =>
              <Link
                key={item?.path}
                to={item?.path}
                className={`px-3 py-2 transition-all duration-300 ${
                isActive(item?.path) ?
                'text-orange-500 font-bold' : 'text-white/70 hover:text-white font-normal'}`
                }>

                  <span className="text-sm text-[rgba(255,252,239,1)]">{item?.label}</span>
                </Link>
              )}
            </nav>

            {/* Right Section: User Avatar with Person Icon */}
            <div className="flex items-center">
              <div className="relative nav-user-menu" ref={userMenuRef}>
                <button
                  className="w-11 h-11 rounded-full flex items-center justify-center transition-all duration-300 overflow-hidden"
                  onClick={() => {
                    const willOpen = !isUserMenuOpen;
                    setIsUserMenuOpen(willOpen);
                    if (willOpen) refreshProfile();
                  }}
                  aria-label="User menu">

                  {avatarUrl ? (
                    <img
                      src={avatarUrl}
                      alt={getUserInitials()}
                      className="w-full h-full object-cover rounded-full"
                      onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'flex'; }}
                    />
                  ) : null}
                  <div
                    className="w-full h-full rounded-full bg-primary text-primary-foreground flex items-center justify-center"
                    style={{ display: avatarUrl ? 'none' : 'flex' }}
                  >
                    {firstName || lastName || email ? (
                      <span className="text-sm font-semibold">{getUserInitials()}</span>
                    ) : (
                      <Icon name="User" size={20} />
                    )}
                  </div>
                </button>

                <div className={`account-popover ${isUserMenuOpen ? 'open' : ''}`}>
                  {/* Credits Usage Section */}
                  <Link
                    to="/user-profile-settings?tab=subscription"
                    className="account-popover-credits block cursor-pointer hover:brightness-110 transition-all"
                    onClick={() => setIsUserMenuOpen(false)}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-white/90 text-sm font-medium">{remainingCredits} / {monthlyCredits} credits left</span>
                      <Icon name="ChevronRight" size={18} className="text-white/30" />
                    </div>
                    <div className="credits-progress-bar">
                      <div
                        className="credits-progress-fill"
                        style={{ width: `${creditPercent}%` }} />
                    </div>
                  </Link>

                  {/* Plan Section */}
                  <div className="account-popover-plan">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className={`text-sm font-medium ${getTierInfo().color} block`}>
                          {getTierInfo().label}
                        </span>
                        <span className="text-white text-sm font-medium block mt-1">
                          {firstName || lastName ? `${firstName} ${lastName}`.trim() : email || 'User'}
                        </span>
                      </div>
                      <Link
                        to="/subscription-payment"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white transition-all duration-300 hover:scale-[1.03] glassmorphic-button"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <Icon name={subscriptionTier === 'team' ? 'RefreshCw' : 'ArrowUp'} size={13} />
                        <span>{subscriptionTier === 'team' ? 'Change' : 'Upgrade'}</span>
                      </Link>
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="account-popover-divider" />

                  {/* Menu Items */}
                  <Link
                    to="/user-profile-settings"
                    className="account-popover-item"
                    onClick={() => setIsUserMenuOpen(false)}>

                    <Icon name="Settings" size={20} className="text-white/50" />
                    <span>Manage account</span>
                  </Link>


                  {/* Divider */}
                  <div className="account-popover-divider" />

                  {/* Sign Out */}
                  <button
                    onClick={handleLogout}
                    className="account-popover-item w-full">

                    <Icon name="LogOut" size={20} className="text-white/50" />
                    <span>Sign out</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>
    </>);

};

export default Header;